import { impactResultSchema, profileSchema, type ImpactResult, type Policy, type Profile } from "@contracts";

const won = new Intl.NumberFormat("ko-KR", {style: "currency", currency: "KRW", maximumFractionDigits: 0});

function relevance(policy: Policy, profile: Profile): Pick<ImpactResult, "relevance" | "relevanceReason" | "needsConfirmation"> {
  if (policy.id.startsWith("POL-WORK")) {
    if (profile.employment === "unknown") return {relevance: "unknown", relevanceReason: "근로 형태를 아직 선택하지 않았습니다.", needsConfirmation: ["임금을 받는 근로자인지 확인"]};
    if (profile.employment !== "employee") return {relevance: "not_applies", relevanceReason: "현재 선택한 근로 형태에는 최저임금 근로자 계산을 적용하지 않았습니다.", needsConfirmation: []};
    return {relevance: "applies", relevanceReason: "임금을 받는 근로자로 선택했습니다.", needsConfirmation: []};
  }
  if (policy.id.startsWith("POL-PENSION")) {
    if (profile.pensionCovered === null) return {relevance: "unknown", relevanceReason: "국민연금 가입 여부를 아직 선택하지 않았습니다.", needsConfirmation: ["국민연금 가입 여부 확인"]};
    if (!profile.pensionCovered) return {relevance: "not_applies", relevanceReason: "현재 국민연금 가입 대상이 아니라고 선택했습니다.", needsConfirmation: []};
    return {relevance: "likely", relevanceReason: "국민연금 가입자 조건과 관련됩니다. 실제 가입 이력은 공단에서 확인해야 합니다.", needsConfirmation: ["국민연금공단 가입 이력 확인"]};
  }
  if (policy.id === "POL-CARE-001") {
    if (profile.disabledChildCare === null) return {relevance: "unknown", relevanceReason: "장애아동 돌봄 여부를 아직 선택하지 않았습니다.", needsConfirmation: ["지원 대상 아동과 가구 기준 확인"]};
    return profile.disabledChildCare
      ? {relevance: "likely", relevanceReason: "장애아동을 돌보는 가구로 선택했습니다.", needsConfirmation: ["소득 기준과 서비스 이용 이력 확인"]}
      : {relevance: "not_applies", relevanceReason: "현재 선택한 가구 조건에는 해당 지원을 적용하지 않았습니다.", needsConfirmation: []};
  }
  return {relevance: "likely", relevanceReason: "선택한 정책군과 관련될 수 있어 공식 적용 조건 확인이 필요합니다.", needsConfirmation: ["공식 기관에서 개인별 적용 조건 확인"]};
}

function qualitative(policy: Policy, base: ReturnType<typeof relevance>): ImpactResult {
  return impactResultSchema.parse({
    policyId: policy.id,
    ...base,
    headline: base.relevance === "not_applies" ? "현재 선택 조건에는 직접 영향이 확인되지 않았어요" : "적용 조건을 한 번 더 확인해야 해요",
    lines: [{
      axis: policy.impactAxes[0],
      label: "확인된 변화",
      before: "현재 개인 조건 미확인",
      after: policy.summary,
      delta: "숫자로 계산하지 않음",
      basis: "검수된 공식 설명만 표시하고 자격·고지액은 확정하지 않습니다.",
      numeric: false
    }],
    assumptions: [],
    sourceIds: policy.sources.map((item) => item.id),
    calculatedAt: new Date().toISOString()
  });
}

export function calculateImpact(policy: Policy, input: Profile): ImpactResult {
  const profile = profileSchema.parse(input);
  const base = relevance(policy, profile);

  if (policy.calculatorId === "minimum_wage_2027") {
    if (profile.monthlyPaidHours === null || base.relevance === "not_applies") {
      return qualitative(policy, {
        ...base,
        needsConfirmation: [...base.needsConfirmation, ...(profile.monthlyPaidHours === null ? ["월 유급시간 확인"] : [])]
      });
    }
    const before = 10_320 * profile.monthlyPaidHours;
    const after = 10_700 * profile.monthlyPaidHours;
    const delta = after - before;
    return impactResultSchema.parse({
      policyId: policy.id,
      ...base,
      headline: `같은 유급시간이면 월 최저임금 기준액이 ${won.format(delta)} 늘어요`,
      lines: [{axis: "money", label: "월 최저임금 기준액", before: won.format(before), after: won.format(after), delta: `+${won.format(delta)}`, basis: `2026년 10,320원과 2027년 10,700원 × 월 유급 ${profile.monthlyPaidHours}시간`, numeric: true}],
      assumptions: ["입력한 월 유급시간이 두 해에 같다고 가정했습니다.", "세전 단순 곱셈이며 주휴·수당·근로계약의 실제 지급액은 별도 확인해야 합니다."],
      sourceIds: policy.sources.map((item) => item.id),
      calculatedAt: new Date().toISOString()
    });
  }

  if (policy.calculatorId === "pension_2026" || policy.calculatorId === "pension_2027") {
    if (profile.monthlyIncomeKrw === null || profile.workplacePension === null || profile.pensionCovered !== true) {
      return qualitative(policy, {
        ...base,
        needsConfirmation: [...base.needsConfirmation, ...(profile.monthlyIncomeKrw === null ? ["기준소득월액 확인"] : []), ...(profile.workplacePension === null ? ["사업장가입 여부 확인"] : [])]
      });
    }
    const fromRate = policy.calculatorId === "pension_2026" ? 0.09 : 0.095;
    const toRate = policy.calculatorId === "pension_2026" ? 0.095 : 0.1;
    const share = profile.workplacePension ? 0.5 : 1;
    const before = profile.monthlyIncomeKrw * fromRate * share;
    const after = profile.monthlyIncomeKrw * toRate * share;
    const delta = after - before;
    return impactResultSchema.parse({
      policyId: policy.id,
      ...base,
      headline: `요율만 반영하면 월 본인 부담이 약 ${won.format(delta)} 늘 수 있어요`,
      lines: [{axis: "money", label: "월 국민연금 본인 부담 추정", before: won.format(before), after: won.format(after), delta: `+${won.format(delta)}`, basis: `${won.format(profile.monthlyIncomeKrw)} × ${(fromRate * 100).toFixed(1)}%→${(toRate * 100).toFixed(1)}% × 본인부담 ${(share * 100).toFixed(0)}%`, numeric: true}],
      assumptions: [profile.workplacePension ? "사업장가입자로 사용자와 근로자가 절반씩 부담한다고 가정했습니다." : "지역가입자로 보험료 전액을 본인이 부담한다고 가정했습니다.", "기준소득월액 상·하한과 공단의 실제 고지·절사 규칙은 반영하지 않은 요율 차이입니다."],
      sourceIds: policy.sources.map((item) => item.id),
      calculatedAt: new Date().toISOString()
    });
  }

  return qualitative(policy, base);
}
